import { useState } from "react";
import { toast } from "sonner";
import {
  actOnContacts,
  errorMessage,
  listCampaigns,
  type ActionResult,
  type Campaign,
  type CampaignContact,
  type ContactAction,
} from "@/api/email";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { ChangeCampaignDialog } from "@/components/email/ChangeCampaignDialog";
import { formatNumber } from "@/lib/formatters";

/** One toast for what an action did, one for what it could not do and why. */
export function reportAction(result: ActionResult, verb: string) {
  if (result.done) toast.success(`${formatNumber(result.done)} ${result.done === 1 ? "contact" : "contacts"} ${verb}`);
  const failures = result.rows.filter((r) => !r.ok && r.error);
  if (failures.length) {
    const reasons = [...new Set(failures.map((f) => f.error))];
    toast.error(`${failures.length} not ${verb}: ${reasons.slice(0, 2).join(" · ")}${reasons.length > 2 ? "…" : ""}`);
  }
}

/**
 * Pause, resume, remove and move leads — the same calls and confirmations
 * wherever a lead is shown (Leads, Launch, Outreach, the drawer). The backend
 * decides; the rows' own project is the one acted in.
 */
export function useContactActions({ external, onChanged }: { external: boolean; onChanged: () => void }) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const [moving, setMoving] = useState<CampaignContact[] | null>(null);
  const [targets, setTargets] = useState<Campaign[]>([]);

  async function act(action: ContactAction, rows: CampaignContact[]) {
    if (!rows.length) return;
    if (action === "remove") {
      const atLemlist = external && rows.some((r) => r.provider_lead_id && !r.dry_run);
      const ok = await confirm({
        title: rows.length === 1 ? `Remove ${rows[0].contact_name}?` : `Remove ${rows.length} contacts?`,
        description: `${
          atLemlist
            ? "They leave the campaign and Lemlist stops their sequence."
            : external
              ? "They leave the campaign. Nothing changes in Lemlist: they were never handed over."
              : "They leave the campaign. Dry run: nothing reaches Lemlist."
        } Their history stays in Prism; they can be added to a campaign again later.`,
        confirmLabel: "Remove",
        variant: "danger",
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const result = await actOnContacts(rows[0].project_slug, action, rows.map((r) => r.id));
      reportAction(result, action === "pause" ? "paused" : action === "resume" ? "resumed" : "removed");
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function move(rows: CampaignContact[]) {
    if (!rows.length) return;
    try {
      // The project's open campaigns: a lead only ever moves within its project.
      const campaigns = await listCampaigns(rows[0].project_slug);
      setTargets(campaigns.filter((c) => c.status !== "ARCHIVED" && !c.provider_legacy));
      setMoving(rows);
    } catch (err) {
      toast.error(errorMessage(err, "Campaigns could not be loaded."));
    }
  }

  const dialogs = moving ? (
    <ChangeCampaignDialog
      slug={moving[0].project_slug}
      rows={moving}
      campaigns={targets}
      onOpenChange={(open) => !open && setMoving(null)}
      onMoved={(result) => {
        reportAction(result, "moved");
        setMoving(null);
        onChanged();
      }}
    />
  ) : null;

  return { busy, act, move, dialogs };
}
